import { MRT_Row } from "material-react-table";
import type { Job } from "../../../../../../api/types";
import { ImportTableColumn } from "../../../../../../utilities/types";
import { isFieldRequired } from "../../../../../../utilities/jobValidation";
import { ColumnContext } from "../ColumnContext";
import { TextColumnViewModel } from "./textColumn.types";

/**
 * Hook that provides the TextColumn ViewModel
 * @param context - The column context containing state and helpers
 * @param columnId - The column identifier for validation
 * @returns ViewModel with all business logic functions
 */
export function useTextColumnViewModel(context: ColumnContext, columnId: ImportTableColumn): TextColumnViewModel {
	/**
	 * Get current text value from job
	 * Uses row.original directly instead of O(n) jobs.find() lookup
	 */
	function getCurrentValue(job: Job, fieldName: keyof Job): string {
		return ((job?.[fieldName] as string) ?? "").toString();
	}

	/**
	 * Compute required state from context
	 */
	function getIsRequired(row: MRT_Row<Job>): boolean {
		const rowContext = context.getRowValidationContext(row);
		return isFieldRequired(columnId, rowContext);
	}

	/**
	 * Compute disabled state
	 */
	function getIsDisabled(row: MRT_Row<Job>, isDisabled?: (row: MRT_Row<Job>) => boolean): boolean {
		return isDisabled ? isDisabled(row) : false;
	}

	/**
	 * Handle text change for Cell
	 */
	function handleTextChange(jobId: string, fieldName: keyof Job, value: string): void {
		const newJobs = [...context.jobs];
		const newJob = newJobs.find((j) => j.id === jobId);
		if (!newJob) return;

		const index = newJobs.indexOf(newJob);
		(newJob as any)[fieldName] = value;
		newJobs[index] = newJob;
		context.setJobs(newJobs);
	}

	/**
	 * Handle text blur for Cell (validates and updates row cache if row provided)
	 * Desktop passes row for MRT cache updates; mobile omits it
	 */
	function handleTextBlur(job: Job, fieldName: keyof Job, row?: MRT_Row<Job>): void {
		// Update row cache for MRT compatibility (desktop only)
		if (row) {
			(row._valuesCache as any)[fieldName] = job[fieldName];
		}
		context.validateJobs([job], { updateState: true, clearPrevious: false });
	}

	/**
	 * Handle group text change for AggregatedCell
	 */
	function handleGroupTextChange(row: MRT_Row<Job>, fieldName: keyof Job, value: string): void {
		const newJobs = context.updateJobGroup(row, context.jobs, (job) => {
			(job as any)[fieldName] = value;
		});

		context.setJobs(newJobs);
		context.validateJobs(newJobs, { updateState: true, clearPrevious: false });
	}

	return {
		getCurrentValue,
		getIsRequired,
		getIsDisabled,
		handleTextChange,
		handleTextBlur,
		handleGroupTextChange,
	};
}
